/**
 * @file applicantFitEngine.js
 * @description Single source-of-truth engine for "how well does this applicant
 *   fit this case?" assessments. Replaces the scattered fit logic that grew
 *   across applicationFit.js, marketReach.js, discoveryFit.js, ApplicantReview.jsx,
 *   ApplicantComparisonView.jsx cells, and RBTProfile.jsx Best Matches.
 *
 *   The drift problem this solves (per architecture audit):
 *     - 5 distinct scorer implementations
 *     - 4 different hours-fit algorithms
 *     - 3 copies of the haversine distance math
 *     - 2 age-range parsers
 *     - Each cell in the applicant summary row computed its own verdict
 *     - Experience / Employment / Payer / Age Group showed in the matrix but
 *       were ignored by the overall % — the 69% didn't reflect them
 *
 *   The engine returns a STRUCTURED assessment, not just a number. Surfaces
 *   then render off the structure without re-implementing the math.
 *
 *   Backward-compat contract:
 *     - The default profile's `.overall` exactly equals the old
 *       applicationFit.computeMatchScore(applicant, listing) value, so
 *       migrating computeMatchScore to a thin wrapper around the engine is
 *       safe (no visual drift on existing surfaces).
 *     - The default profile's `.verdict` exactly equals the old
 *       applicationFit.computeMatchSummary().tier value, with the same
 *       summary/recommendation copy.
 *
 *   Weight profiles (each caller picks the one that fits its use case):
 *     - AGENCY_REVIEW (default) — Skills 40 / Setting 20 / Schedule 20 /
 *       Distance 20. Mirrors applicationFit.js A.
 *     - MARKETPLACE      — Role 30 / Skills 30 / Hours 20 / Setting 20.
 *       Mirrors marketReach.matchScore B.
 *     - DISCOVERY_RBT    — Skills 36 / Employment 13 / Setting 13 /
 *       Distance 18 / Freshness 10 / Payer 10. Mirrors RBTProfile RBT path.
 *     - DISCOVERY_BCBA   — Skills+Assessments 25 / Employment 10 / Setting 10 /
 *       Distance 12 / Freshness 5 / Caseload 13 / Billable hrs 15 / Payer 10.
 *       Mirrors RBTProfile BCBA path.
 *
 *   This file ONLY contains the engine. Migration of individual callers
 *   happens in subsequent sub-commits (3.2 onwards).
 *
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/applicantFitEngine
 */
import {
  withinDistance,
  ageGroupOverlapFraction,
} from "./matchScoring";
import { periodsFor } from "./schedulePeriods";
import { typeLabel } from "../theme";

/** Verdict tiers used per-dimension and at the top level. */
export const VERDICTS = Object.freeze({
  STRONG:  "strong",   // green — full match
  PARTIAL: "partial",  // yellow — partial / soft match
  WEAK:    "weak",     // red — explicit miss on a required dimension
  NA:      "na",       // grey — dimension not applicable (case didn't specify)
});

/** Top-level tier (banner verdict) — same vocabulary applicationFit used. */
export const TIERS = Object.freeze({
  STRONG: "strong",
  REVIEW: "review",
  PASS:   "pass",
});

/** Match-tier threshold reused by sorting/strong-pill across the app. */
export const STRONG_MATCH_THRESHOLD = 70;
/** Below this the fit reads "Low"; between this and STRONG it reads "Review". The one place these live. */
export const REVIEW_MATCH_THRESHOLD = 40;

/**
 * Score -> tier, the single rule every surface uses (agency list, profile window, compare, provider "your fit").
 * @param {number|null} score
 * @returns {"strong"|"review"|"pass"|null}
 */
export function fitTierForScore(score) {
  if (score == null || Number.isNaN(score)) return null;
  if (score >= STRONG_MATCH_THRESHOLD) return TIERS.STRONG;
  if (score >= REVIEW_MATCH_THRESHOLD) return TIERS.REVIEW;
  return TIERS.PASS;
}

/**
 * Requirements a case treats as dealbreakers. Agencies mark them when posting (cases.must_haves, owner approved
 * board 4, 2026-10-18); a case without that list (null) uses these defaults. Each applies only when the case
 * actually specifies it. The credential is always a must have. Pinned key skills (cases.must_have_skills) add a
 * "keySkills" must have. Mirrors the DB checks cases_must_haves_check and cases_must_have_skills_max.
 */
export const DEFAULT_MUST_HAVES = Object.freeze(["credential", "schedule", "language"]); // owner approved 2026-10-18
export const OPTIONAL_MUST_HAVES = Object.freeze(["schedule", "language", "payer", "setting"]);
export const MAX_KEY_SKILLS = 3;
/** The agency's own choice among OPTIONAL_MUST_HAVES, or null when the case uses the defaults. */
export function chosenMustHaves(listing) {
  return Array.isArray(listing?.mustHaves) ? listing.mustHaves.filter(k => OPTIONAL_MUST_HAVES.includes(k)) : null;
}
/** Pinned key skills that are still among the case skills (a skill removed later never caps anyone). */
export function keySkillsFor(listing) {
  const pins = Array.isArray(listing?.mustHaveSkills) ? listing.mustHaveSkills : [];
  if (!pins.length) return [];
  const skills = pickSkills(listing);
  return pins.filter(p => skills.some(s => normToken(s) === normToken(p))).slice(0, MAX_KEY_SKILLS);
}
export function mustHavesFor(listing) {
  const own = chosenMustHaves(listing);
  const base = own ? ["credential", ...own] : [...DEFAULT_MUST_HAVES];
  return keySkillsFor(listing).length ? [...base, "keySkills"] : base;
}
/** The stored must-have choice after marking one requirement Must have (on) or Nice to have (off). */
export function withMustHave(listing, key, on) {
  if (!OPTIONAL_MUST_HAVES.includes(key)) return chosenMustHaves(listing);
  const current = chosenMustHaves(listing) ?? DEFAULT_MUST_HAVES.filter(k => k !== "credential");
  const next = on ? [...new Set([...current, key])] : current.filter(k => k !== key);
  return OPTIONAL_MUST_HAVES.filter(k => next.includes(k));
}
/** The pinned key skills after pinning (on) or unpinning one. Pinning past MAX_KEY_SKILLS changes nothing. */
export function withKeySkill(listing, skill, on) {
  const pins = keySkillsFor(listing);
  if (!on) return pins.filter(p => p !== skill);
  if (pins.includes(skill) || pins.length >= MAX_KEY_SKILLS) return pins;
  return [...pins, skill];
}

/**
 * Default weight profile — exactly matches applicationFit.computeMatchScore.
 * The other dimensions (exp/employ/payer/age/hours) are computed and surfaced
 * in the dimensions object but excluded from the overall % under this profile
 * so the migration of A is a no-op on the overall number.
 */
/**
 * Verification bonus + over-radius distance penalty used by the legacy
 * marketReach.matchScore. Captured here as the MARKETPLACE post-process so
 * the engine can reproduce the exact same final score.
 *
 *   +15 if therapist.verificationStatus === "verified"
 *   −up-to-20 if therapist.distanceMi is provided AND it exceeds
 *     therapist.maxDistance (linear ramp: 1× over = 0 penalty, 2× over = full
 *     20-pt penalty). When distanceMi is null we skip the penalty.
 *   Result clamped to 0..100.
 */
function marketplacePostProcess(base, { applicant }) {
  const verBonus = applicant?.verificationStatus === "verified" ? 15 : 0;
  if (applicant?.distanceMi != null) {
    const maxDist    = Math.max(1, parseInt(applicant.maxDistance, 10) || 20);
    const actualDist = applicant.distanceMi;
    if (actualDist > maxDist) {
      const excess  = Math.min(1, (actualDist - maxDist) / maxDist);
      const penalty = Math.round(excess * 20);
      return Math.max(0, Math.min(100, base + verBonus - penalty));
    }
  }
  return Math.min(100, base + verBonus);
}

export const PROFILES = Object.freeze({
  AGENCY_REVIEW: {
    // 2026-10-18 (owner approved): days and time 25, skills 30, distance 15, language 10, payer 10, setting and
    // hours 10. The credential is a gate (caps below), not a weight. Unknown dimensions drop out of the denominator.
    weights: { schedule: 25, skills: 30, distance: 15, language: 10, payer: 10, setting: 5, hours: 5 },
    // A missed must have caps the score just under the Review line, so it always reads "Low".
    mustHaveCap: REVIEW_MATCH_THRESHOLD - 1,
    // A must have that is only partly met (verification pending, some sessions not covered) or not filled in by the
    // provider (owner decision 2026-10-18: unconfirmed is not proof of a fit) tops out at Review.
    mustHavePartialCap: STRONG_MATCH_THRESHOLD - 1,
    // A wrong-credential applicant (e.g. an RBT on a BCBA/BCaBA case) can't fill
    // the role, so cap the overall fit low — it must never read as a peer of
    // correctly-credentialed candidates. Role isn't a weighted dimension here
    // (the queue is normally split by case type); this is the hard guard for
    // the cross-post case. Only fires on a CONFIRMED mismatch (see assessRole).
    roleMismatchCap: 25,
  },
  MARKETPLACE: {
    weights: { skills: 30, hours: 20, setting: 20, role: 30 },
    // Legacy matchScore semantics:
    //   - Hours: binary in/out of range (PARTIAL → 0, not 0.5).
    //   - Setting: skip the dimension entirely (excluded from denominator)
    //     when the applicant has no setting preferences — don't penalize a
    //     therapist who hasn't filled in their settings.
    scoreDimensions: {
      hours: (dim) => {
        if (!dim || !dim.applicable) return null;
        return dim.verdict === VERDICTS.STRONG ? 1 : 0;
      },
      setting: (dim) => {
        if (!dim || !dim.applicable) return null;
        if ((dim.applicantHas || []).length === 0) return null;
        return dimensionScore01(dim);
      },
    },
    postProcess: marketplacePostProcess,
  },
  DISCOVERY_RBT: {
    weights: { skills: 36, employ: 13, setting: 13, distance: 18, freshness: 10, payer: 10 },
  },
  DISCOVERY_BCBA: {
    weights: { skills: 25, employ: 10, setting: 10, distance: 12, freshness: 5, caseload: 13, billable: 15, payer: 10 },
  },
});

// ──────────────────────────────────────────────────────────────────────────────
// Per-dimension assessors. Each takes the applicant + listing and returns a
// structured dimension object: the raw inputs the UI needs to render, plus a
// `verdict` (strong/partial/weak/na) and an `applicable` flag.
// ──────────────────────────────────────────────────────────────────────────────

function asArray(v) {
  return Array.isArray(v) ? v : v ? [v] : [];
}

// Token normalization for case-/separator-insensitive matching. Therapist and
// case vocabularies drift in casing and separators ("School" vs "school",
// "Early Intervention" vs "early intervention", "W2" vs "w2", "full_time" vs
// "full-time"), and exact .includes() was scoring those as misses. Normalize to
// lowercase with whitespace/._- collapsed to a single space before comparing.
const normToken = (s) => String(s ?? "").trim().toLowerCase().replace(/[\s._-]+/g, " ");

// Some rows arrive as a single comma/slash-joined string ("clinic, school",
// "W2, 1099", "Home/School") instead of separate array elements — bad data, but
// it shouldn't read as a miss. expand() flattens those into atomic tokens so the
// matchers compare like-for-like.
const SPLIT_RE = /\s*[,;/|]\s*/;
const expand = (arr) => (arr || []).flatMap(x => String(x ?? "").split(SPLIT_RE)).map(s => s.trim()).filter(Boolean);

// Set-overlap helpers that compare on normalized, separator-split tokens but
// RETURN the original canonical labels (from `required`/`has`) so the UI still
// shows "School", "DTT". A required element matches when ANY of its expanded
// tokens is present on the applicant side, keeping matched ⊆ required (counts
// stay honest even when one side is a joined string).
function matchedBy(required, has) {
  const hasSet = new Set(expand(has).map(normToken));
  return (required || []).filter(r => expand([r]).some(tok => hasSet.has(normToken(tok))));
}
function missingBy(required, has) {
  const hasSet = new Set(expand(has).map(normToken));
  return (required || []).filter(r => !expand([r]).some(tok => hasSet.has(normToken(tok))));
}
function extraBy(has, required) {
  const reqSet = new Set(expand(required).map(normToken));
  return (has || []).filter(h => !expand([h]).some(tok => reqSet.has(normToken(tok))));
}

// Canonical employment vocabulary the app actually uses (tax classification).
// full_time / part_time are a *commitment* axis, not an employment type, and
// appear only in stale seed data — an applicant carrying only those has stated
// no W2/1099 preference, so employment fit is unknown (NA), not a hard miss.
const EMPLOYMENT_VOCAB = new Set(["w2", "1099"]);

function pickSkills(listing)   { return listing?.skills || listing?.requiredSkills || []; }
function pickSettings(listing) { return asArray(listing?.settings).length ? asArray(listing?.settings) : asArray(listing?.setting); }
function pickDays(listing)     { return asArray(listing?.availability).length ? asArray(listing.availability) : asArray(listing?.serviceDays); }
function pickEmployments(o)    { return asArray(o?.employments).length ? asArray(o?.employments) : asArray(o?.employment); }

function assessSkills(applicant, listing) {
  const required = pickSkills(listing);
  const has = applicant?.skills || [];
  if (required.length === 0) return { applicable: false, verdict: VERDICTS.NA, caseRequires: [], applicantHas: has, matched: [], missing: [], extra: has };
  const matched = matchedBy(required, has);
  const missing = missingBy(required, has);
  const extra   = extraBy(has, required);
  const frac    = matched.length / required.length;
  let verdict;
  if (frac >= 1)        verdict = VERDICTS.STRONG;
  else if (frac >= 0.5) verdict = VERDICTS.PARTIAL;
  else if (frac > 0)    verdict = VERDICTS.PARTIAL;
  else                  verdict = VERDICTS.WEAK;
  return { applicable: true, verdict, caseRequires: required, applicantHas: has, matched, missing, extra, fraction: frac };
}

// Key skills: the skills the agency pinned as must haves. All of them is met, some is partly met, none is a miss.
// A provider who listed no skills at all is not provided (neutral), never a miss.
function assessKeySkills(applicant, listing) {
  const required = keySkillsFor(listing);
  const has = applicant?.skills || [];
  if (required.length === 0) return { applicable: false, verdict: VERDICTS.NA, caseRequires: [], applicantHas: has, matched: [], missing: [] };
  if (has.length === 0) return { applicable: true, verdict: VERDICTS.NA, caseRequires: required, applicantHas: [], matched: [], missing: required };
  const matched = matchedBy(required, has);
  const missing = missingBy(required, has);
  const fraction = matched.length / required.length;
  const verdict = fraction >= 1 ? VERDICTS.STRONG : fraction > 0 ? VERDICTS.PARTIAL : VERDICTS.WEAK;
  return { applicable: true, verdict, caseRequires: required, applicantHas: has, matched, missing, fraction };
}

function assessSetting(applicant, listing) {
  const required = pickSettings(listing);
  const has = applicant?.settings || [];
  if (required.length === 0) return { applicable: false, verdict: VERDICTS.NA, caseWants: [], applicantHas: has, matched: [], extra: has };
  const matched = matchedBy(required, has);
  const extra   = extraBy(has, required);
  // applicationFit A: empty-applicant-settings counts as a non-match (weak).
  let verdict;
  if (!has || has.length === 0) verdict = VERDICTS.NA;
  else if (matched.length === required.length) verdict = VERDICTS.STRONG;
  else if (matched.length > 0) verdict = VERDICTS.PARTIAL;
  else                       verdict = VERDICTS.WEAK;
  return { applicable: true, verdict, caseWants: required, applicantHas: has, matched, extra, overlap: matched.length > 0 };
}

function assessSchedule(applicant, listing) {
  const required = pickDays(listing);
  // What the applicant committed to for THIS case wins over their profile defaults.
  const has = (Array.isArray(applicant?.appliedAvailDays) && applicant.appliedAvailDays.length)
    ? applicant.appliedAvailDays : (applicant?.availability || []);
  const appPeriods = (Array.isArray(applicant?.appliedAvailPeriods) && applicant.appliedAvailPeriods.length)
    ? applicant.appliedAvailPeriods : (applicant?.availabilityPeriods || []);
  if (required.length === 0) return { applicable: false, verdict: VERDICTS.NA, caseWants: [], applicantHas: has, matched: [], missing: [], extra: has, sessions: [] };
  const dayMatched = new Set(matchedBy(required, has));
  const periodSet = new Set(expand(appPeriods).map(normToken));
  const timeKnown = periodSet.size > 0;
  const anyTime = periodSet.has("flexible");
  // One session per case day: covered when the applicant has the day AND (when the case names a time of day
  // and the applicant told us theirs) every needed period. An unstated time of day never counts against them.
  const sessions = required.map((day) => {
    const needs = periodsFor(listing?.servicePeriods, day);
    const flexibleCase = needs.length === 0 || needs.includes("Flexible");
    const dayOk = dayMatched.has(day);
    const timeOk = flexibleCase || !timeKnown || anyTime || needs.every(p => periodSet.has(normToken(p)));
    return { day, needs, dayOk, timeOk, covered: dayOk && timeOk };
  });
  const matched = sessions.filter(x => x.covered).map(x => x.day);
  const missing = sessions.filter(x => !x.covered).map(x => x.day);
  const extra   = extraBy(has, required);
  const timeMiss = sessions.some(x => x.dayOk && !x.timeOk);
  const frac    = (!has || has.length === 0) ? null : matched.length / required.length;
  let verdict;
  if (frac == null)       verdict = VERDICTS.NA;
  else if (frac >= 1)     verdict = VERDICTS.STRONG;
  else if (frac > 0)      verdict = VERDICTS.PARTIAL;
  else                    verdict = VERDICTS.WEAK;
  return { applicable: true, verdict, caseWants: required, applicantHas: has, matched, missing, extra, fraction: frac,
    sessions, timeKnown, timeMiss, applicantPeriods: appPeriods };
}

function assessHours(applicant, listing) {
  // Parse "35h/wk" or just "35" or "30-40" etc. into a single midpoint number
  // for the case, and an interval for the applicant.
  const parseSingle = (s) => {
    if (s == null) return null;
    const m = String(s).match(/(\d+)\s*[–-]\s*(\d+)/);
    if (m) return (parseInt(m[1], 10) + parseInt(m[2], 10)) / 2;
    const n = parseInt(String(s).match(/\d+/)?.[0] ?? "", 10);
    return Number.isFinite(n) ? n : null;
  };
  const caseHours = parseSingle(listing?.hours);
  const minH = Number.isFinite(applicant?.minHours) ? applicant.minHours : null;
  const maxH = Number.isFinite(applicant?.maxHours) ? applicant.maxHours : null;
  if (caseHours == null) {
    return { applicable: false, verdict: VERDICTS.NA, caseWants: null, applicantRange: [minH, maxH] };
  }
  if (minH == null && maxH == null) {
    return { applicable: true, verdict: VERDICTS.NA, caseWants: caseHours, applicantRange: [null, null] };
  }
  const inRange = (minH == null || caseHours >= minH) && (maxH == null || caseHours <= maxH);
  return { applicable: true, verdict: inRange ? VERDICTS.STRONG : VERDICTS.PARTIAL, caseWants: caseHours, applicantRange: [minH, maxH], inRange };
}

function assessExperience(applicant, listing) {
  const required = listing?.experienceMin;
  const has = applicant?.experience;
  if (required == null) return { applicable: false, verdict: VERDICTS.NA, caseMin: null, applicantHas: has ?? null, meets: null };
  // Not provided is unknown, never a miss.
  if (has == null) return { applicable: true, verdict: VERDICTS.NA, caseMin: required, applicantHas: null, meets: null };
  const meets = has >= required;
  return { applicable: true, verdict: meets ? VERDICTS.STRONG : VERDICTS.WEAK, caseMin: required, applicantHas: has, meets };
}

function assessDistance(applicant, listing) {
  // Two distance signals exist on data: applicant.distanceMi (haversine to
  // the case, computed upstream) vs the relevant max — applicationFit A uses
  // listing.maxDistance; ApplicantComparisonView's DistanceCell uses
  // applicant.maxDistance. The audit flagged this divergence. The engine
  // prefers listing.maxDistance (case requirement) and falls back to
  // applicant.maxDistance only when the case didn't set one — that mirrors
  // applicationFit A, which is the canonical Applications-screen scorer.
  const distance = Number.isFinite(applicant?.distanceMi) ? applicant.distanceMi : null;
  const max = listing?.maxDistance ?? listing?.distanceMax ?? applicant?.maxDistance ?? null;
  const within = withinDistance(distance, max);
  if (within === null) {
    return { applicable: false, verdict: VERDICTS.NA, applicantMi: distance, maxMi: max, withinRadius: null };
  }
  return { applicable: true, verdict: within ? VERDICTS.STRONG : VERDICTS.WEAK, applicantMi: distance, maxMi: max, withinRadius: within };
}

function assessEmployment(applicant, listing) {
  const caseEmps = pickEmployments(listing);
  const appEmpsRaw = pickEmployments(applicant);
  if (caseEmps.length === 0) return { applicable: false, verdict: VERDICTS.NA, caseWants: [], applicantWants: appEmpsRaw, matched: [] };
  // Keep only applicant values that are in the recognized employment vocabulary.
  // Stale commitment terms (full_time/part_time) aren't a W2/1099 signal, so an
  // applicant carrying only those reads as "no stated preference" (NA), not a miss.
  const appEmps = expand(appEmpsRaw).filter(e => EMPLOYMENT_VOCAB.has(normToken(e)));
  if (appEmps.length === 0)  return { applicable: true,  verdict: VERDICTS.NA, caseWants: caseEmps, applicantWants: [], matched: [] };
  const matched = matchedBy(caseEmps, appEmps);
  let verdict;
  if (matched.length > 0)               verdict = VERDICTS.STRONG;
  else                                  verdict = VERDICTS.WEAK;
  return { applicable: true, verdict, caseWants: caseEmps, applicantWants: appEmps, matched };
}

function assessAgeGroup(applicant, listing) {
  // A case's range is its client's age band; a position's is the ages its program serves (absent when
  // the agency left it blank, which makes the dimension not applicable, exactly like a case without one).
  const caseRange = listing?.ageRange || listing?.ageGroup || null;
  const groups = applicant?.ageGroups || [];
  if (!caseRange || groups.length === 0) return { applicable: false, verdict: VERDICTS.NA, caseRange, applicantGroups: groups, fraction: null };
  const frac = ageGroupOverlapFraction(caseRange, groups);
  let verdict;
  if (frac == null)    verdict = VERDICTS.NA;
  else if (frac >= 1)  verdict = VERDICTS.STRONG;
  else if (frac > 0)   verdict = VERDICTS.PARTIAL;
  else                 verdict = VERDICTS.WEAK;
  return { applicable: true, verdict, caseRange, applicantGroups: groups, fraction: frac };
}

// Canonical role bucket from a type or credential string. Order matters:
// BCABA must be tested before BCBA ("BCABA".startsWith("BCBA") is false, but a
// naive BCBA-vs-everything-else split mis-bucketed BCaBA as RBT). Returns null
// for anything we can't confidently classify, so role mismatch is only asserted
// when BOTH sides are known.
export function roleBucket(s) {
  if (!s) return null;
  const u = String(s).toUpperCase().replace(/[^A-Z]/g, "");
  if (u.startsWith("BCABA")) return "BCABA";
  if (u.startsWith("BCBA"))  return "BCBA";
  if (u.startsWith("RBT"))   return "RBT";
  if (u === "BT")            return "BT";
  return null;
}

function assessRole(applicant, listing) {
  // Role match across RBT / BCBA / BCaBA. Listing has `type`; applicant exposes
  // either an explicit `type` or a `credential` string. A confirmed mismatch
  // (both roles known and different) is a hard signal — an RBT can't fill a
  // BCBA/BCaBA case and vice-versa — and is used to cap the agency-review score.
  const caseType = roleBucket(listing?.type);
  if (!caseType) return { applicable: false, verdict: VERDICTS.NA, caseType: null, applicantType: null };
  // Credential first: in the agency queue, loadCaseApplicants sets
  // applicant.type = the CASE's type (applicationService dbToApplicant), so it
  // always "matches" and is useless for detecting a role mismatch. The
  // therapist's real certification is in `credential` (e.g. "RBT-23-…",
  // "BCBA-…", "BCaBA-…"). Fall back to type only when there's no credential.
  const appType = roleBucket(applicant?.credential) || roleBucket(applicant?.type);
  if (!appType) return { applicable: true, verdict: VERDICTS.NA, caseType, applicantType: null };
  // RBT applicant on a BT case: RBT is a superset of BT requirements — treat as strong match
  const verdict = (appType === caseType || (caseType === "BT" && appType === "RBT"))
    ? VERDICTS.STRONG : VERDICTS.WEAK;
  return { applicable: true, verdict, caseType, applicantType: appType };
}

// Credential: the right certification AND verified by ABAcases (owner rules, 2026-10-18).
//   verified                  met
//   wrong role                miss (near-disqualifying cap)
//   rejected, revoked         miss: ABAcases reviewed it and it failed
//   not submitted, pending    warning (partial): they may hold the credential, it is just not confirmed yet
//   expired                   warning (partial): ask them to renew
//   BT providers              not considered: a BT has no certification to verify
const CRED_OK      = new Set(["verified"]);
const CRED_FAILED  = new Set(["rejected", "revoked"]);
const isBtProvider = (applicant) => roleBucket(applicant?.credentialKind) === "BT" || roleBucket(applicant?.credential) === "BT";
function assessCredential(applicant, listing) {
  if (isBtProvider(applicant)) return { applicable: false, verdict: VERDICTS.NA, status: null, reason: "bt" };
  const role = assessRole(applicant, listing);
  const status = String(applicant?.verificationStatus || "").toLowerCase() || null;
  if (!role.applicable && !status) return { applicable: false, verdict: VERDICTS.NA, status: null, role };
  if (role.applicable && role.verdict === VERDICTS.WEAK) return { applicable: true, verdict: VERDICTS.WEAK, status, role, reason: "wrong_role" };
  if (status && CRED_OK.has(status))     return { applicable: true, verdict: VERDICTS.STRONG, status, role };
  if (status && CRED_FAILED.has(status)) return { applicable: true, verdict: VERDICTS.WEAK, status, role, reason: status };
  const reason = !status || status === "unverified" || status === "not_submitted" ? "unverified" : status === "expired" ? "expired" : "pending";
  return { applicable: true, verdict: VERDICTS.PARTIAL, status: status || "unverified", role, reason, fraction: 0.5 };
}

// Language: the case lists the client's family languages (posting form, client details). The provider meets it by
// speaking ANY of them, English included. A case that lists only English is not a requirement (nearly everyone
// qualifies). An agency that needs a specific language marks it as a must have when posting (listing.languageRequired).
function assessLanguage(applicant, listing) {
  const listed = asArray(listing?.requiredLanguages).length ? asArray(listing.requiredLanguages) : asArray(listing?.languages);
  const has = asArray(applicant?.languages);
  if (listed.length === 0 || listed.every(l => normToken(l) === "english")) {
    return { applicable: false, verdict: VERDICTS.NA, caseWants: listed, applicantHas: has, matched: [] };
  }
  if (has.length === 0) return { applicable: true, verdict: VERDICTS.NA, caseWants: listed, applicantHas: [], matched: [] }; // not provided
  const matched = matchedBy(listed, has);
  return { applicable: true, verdict: matched.length > 0 ? VERDICTS.STRONG : VERDICTS.WEAK, caseWants: listed, applicantHas: has, matched };
}

function assessPayer(applicant, listing) {
  const required = asArray(listing?.payerTypes).length ? asArray(listing?.payerTypes) : asArray(listing?.payers);
  const has = applicant?.payerEnrollments || [];
  if (required.length === 0) return { applicable: false, verdict: VERDICTS.NA, caseWants: [], applicantHas: has, matched: [] };
  if (has.length === 0)      return { applicable: true,  verdict: VERDICTS.NA, caseWants: required, applicantHas: [], matched: [] }; // not provided
  const matched = matchedBy(required, has);
  let verdict;
  if (matched.length === required.length) verdict = VERDICTS.STRONG;
  else if (matched.length > 0)            verdict = VERDICTS.PARTIAL;
  else                                    verdict = VERDICTS.WEAK;
  return { applicable: true, verdict, caseWants: required, applicantHas: has, matched };
}

/** Must-have dimension keys this case applies to the applicant, with their verdicts. */
export function mustHaveResults(dimensions, listing) {
  return mustHavesFor(listing)
    .filter(k => dimensions?.[k]?.applicable)
    .map(k => ({ key: k, verdict: dimensions[k].verdict }));
}
/** Must haves the applicant clearly misses (unknown is not a miss). */
export function missedMustHaves(dimensions, listing) {
  return mustHaveResults(dimensions, listing).filter(r => r.verdict === VERDICTS.WEAK).map(r => r.key);
}
/** Must haves the provider did not fill in: never a miss, but not confirmed either, so the score stays at Review. */
export function unconfirmedMustHaves(dimensions, listing) {
  return mustHaveResults(dimensions, listing).filter(r => r.verdict === VERDICTS.NA).map(r => r.key);
}

// ──────────────────────────────────────────────────────────────────────────────
// Overall score + verdict assembly
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Convert a dimension to a 0..1 score contribution. STRONG=1, PARTIAL uses
 * the fractional data when available, WEAK=0, NA contributes nothing (and
 * its weight is excluded from the denominator).
 */
function dimensionScore01(dim) {
  if (!dim || dim.verdict === VERDICTS.NA) return null;
  switch (dim.verdict) {
    case VERDICTS.STRONG:  return 1;
    case VERDICTS.WEAK:    return 0;
    case VERDICTS.PARTIAL: {
      // Prefer the dimension's fractional data; fall back to 0.5.
      if (typeof dim.fraction === "number" && !Number.isNaN(dim.fraction)) {
        return dim.fraction;
      }
      return 0.5;
    }
    default: return null;
  }
}

function buildSummaryAndRecommendation(dimensions, listing, { mustHaves = false } = {}) {
  // Mirrors applicationFit.computeMatchSummary tiering exactly, so the
  // existing banner copy doesn't drift after migration.
  const { skills, setting, schedule, role, credential } = dimensions;
  // Wrong credential is a hard mismatch — surface it first so a capped low score
  // is self-explanatory rather than a mysterious number.
  if (role?.applicable && role.verdict === VERDICTS.WEAK) {
    const labelRole = typeLabel;
    return {
      tier: TIERS.PASS,
      summary: `Wrong credential: ${labelRole(role.applicantType)} applicant for a ${labelRole(role.caseType)} case.`,
      recommendation: "Likely pass. Credential doesn't match the case.",
    };
  }
  // Must haves come before skills: the explanation must agree with the capped score.
  if (mustHaves && credential?.applicable && credential.verdict === VERDICTS.WEAK) {
    return {
      tier: TIERS.PASS,
      summary: `Credential ${credential.status === "revoked" ? "revoked" : "rejected"}.`,
      recommendation: "Ask them to update their credential before advancing.",
    };
  }
  const MUST_LABEL = { schedule: "days and time", language: "language", payer: "payer", skills: "skills", setting: "setting" };
  const missed = mustHaves ? missedMustHaves(dimensions, listing).filter(k => k !== "credential") : [];
  if (missed.length > 0) {
    return {
      tier: TIERS.PASS,
      summary: `Misses a must have: ${missed.map(k => MUST_LABEL[k] || k).join(", ")}.`,
      recommendation: "Likely pass unless they can change their availability or the case can flex.",
    };
  }
  const reqSkillsLen = skills?.caseRequires?.length || 0;
  const missing      = skills?.missing || [];
  const skillsOk     = reqSkillsLen === 0 || missing.length === 0;
  const partialSkills = reqSkillsLen > 0 && missing.length > 0 && missing.length < reqSkillsLen;
  const settingMatch = !setting?.applicable || setting.verdict !== VERDICTS.WEAK;
  const scheduleOk   = !schedule?.applicable || schedule.verdict !== VERDICTS.WEAK;

  if (skillsOk && settingMatch && scheduleOk) {
    return {
      tier: TIERS.STRONG,
      summary: reqSkillsLen > 0
        ? `Meets all ${reqSkillsLen} required skill${reqSkillsLen > 1 ? "s" : ""} and aligns with the preferred setting.`
        : "Aligns with setting and schedule requirements.",
      recommendation: "Advance to interview.",
    };
  }
  if (partialSkills && settingMatch) {
    const names = missing.slice(0, 3).join(", ") + (missing.length > 3 ? "…" : "");
    return {
      tier: TIERS.REVIEW,
      summary: `Strong on setting, but missing ${missing.length} of ${reqSkillsLen} required skill${missing.length > 1 ? "s" : ""}: ${names}.`,
      recommendation: "Review manually before advancing.",
    };
  }
  if (!skillsOk && reqSkillsLen > 0) {
    const names = missing.slice(0, 3).join(", ") + (missing.length > 3 ? "…" : "");
    return {
      tier: TIERS.PASS,
      summary: missing.length === reqSkillsLen
        ? `Missing all ${reqSkillsLen} required skills.`
        : `Missing ${missing.length} required skill${missing.length > 1 ? "s" : ""}: ${names}.`,
      recommendation: "Likely pass unless additional context changes the fit.",
    };
  }
  return {
    tier: TIERS.REVIEW,
    summary: !settingMatch ? "Setting does not match the case requirement." : "Some requirements are unspecified. Further review needed.",
    recommendation: "Review manually before advancing.",
  };
}

/**
 * Compute all dimensions in one pass. Order matches the existing summary-row
 * cell order in ApplicantComparisonView for easy migration.
 */
function computeDimensions(applicant, listing) {
  return {
    skills:    assessSkills(applicant, listing),
    keySkills: assessKeySkills(applicant, listing),
    setting:   assessSetting(applicant, listing),
    schedule:  assessSchedule(applicant, listing),
    hours:     assessHours(applicant, listing),
    exp:       assessExperience(applicant, listing),
    distance:  assessDistance(applicant, listing),
    employ:    assessEmployment(applicant, listing),
    age:       assessAgeGroup(applicant, listing),
    payer:     assessPayer(applicant, listing),
    role:      assessRole(applicant, listing),
    credential: assessCredential(applicant, listing),
    language:  assessLanguage(applicant, listing),
  };
}

/**
 * Compute the weighted overall % using the given profile's weight table.
 * Only dimensions listed in `profile.weights` count toward the score; the
 * others are still computed and surfaced for the UI, but excluded from the
 * overall denominator so the score behaves identically to the legacy
 * computeMatchScore for that profile.
 */
/**
 * The overall % plus how it was built: each weighted part (points earned of points possible, after dropping
 * unknown dimensions from the denominator) and any cap applied. Drives the "Why 92" breakdown.
 * @returns {{ score: number|null, parts: Array<{key, weight, earned, possible, score01}>, caps: string[] }}
 */
/** Round values to whole numbers that add up to `total` (largest remainder). */
function apportion(values, total) {
  const floors = values.map(Math.floor);
  let left = total - floors.reduce((a, b) => a + b, 0);
  const order = values.map((v, i) => [v - floors[i], i]).sort((a, b) => b[0] - a[0]);
  for (let k = 0; left > 0 && k < order.length; k++, left--) floors[order[k][1]] += 1;
  return floors;
}

function computeOverallDetailed(dimensions, profile, ctx) {
  const weights         = profile?.weights         || PROFILES.AGENCY_REVIEW.weights;
  const scoreDimensions = profile?.scoreDimensions || {};
  let scoreSum  = 0;
  let weightSum = 0;
  const raw = [];
  for (const [key, w] of Object.entries(weights)) {
    const dim = dimensions[key];
    // Profile can override the verdict→0..1 mapping per dimension. Returning
    // null from the custom scorer EXCLUDES the dimension from the denominator
    // (used e.g. by MARKETPLACE to skip setting when the applicant has no
    // setting preferences, matching the legacy matchScore behavior).
    const custom = scoreDimensions[key];
    const s = custom ? custom(dim, ctx) : dimensionScore01(dim);
    if (s == null) continue;
    scoreSum  += s * w;
    weightSum += w;
    raw.push({ key, weight: w, score01: s });
  }
  if (weightSum === 0) return { score: null, parts: [], caps: [] };
  let base = Math.round((scoreSum / weightSum) * 100);
  // Points out of 100: each part's share of the weights actually counted, rounded so the possible points add up
  // to 100 and the earned points add up to the score before any cap ("Why 92" must add up).
  const possible = apportion(raw.map(r => (r.weight / weightSum) * 100), 100);
  const earned = apportion(raw.map(r => (r.weight / weightSum) * 100 * r.score01), base);
  const parts = raw.map((r, i) => ({ ...r, possible: possible[i], earned: Math.min(earned[i], possible[i]) }));
  const caps = [];
  // Hard role-mismatch cap (see PROFILES.AGENCY_REVIEW.roleMismatchCap): a
  // confirmed wrong-credential applicant is near-disqualified regardless of how
  // well the other dimensions score.
  if (typeof profile?.roleMismatchCap === "number"
      && dimensions.role?.applicable
      && dimensions.role.verdict === VERDICTS.WEAK) {
    if (base > profile.roleMismatchCap) caps.push("wrong_role");
    base = Math.min(base, profile.roleMismatchCap);
  }
  // Credential failure (wrong role, or verification rejected/revoked) is near-disqualifying, like a role
  // mismatch. Any other missed must have caps the score at mustHaveCap.
  if (typeof profile?.roleMismatchCap === "number"
      && dimensions.credential?.applicable && dimensions.credential.verdict === VERDICTS.WEAK) {
    if (base > profile.roleMismatchCap) caps.push("credential");
    base = Math.min(base, profile.roleMismatchCap);
  }
  if (typeof profile?.mustHaveCap === "number" && missedMustHaves(dimensions, ctx?.listing).length > 0) {
    if (base > profile.mustHaveCap) caps.push("must_have_missed");
    base = Math.min(base, profile.mustHaveCap);
  }
  if (typeof profile?.mustHavePartialCap === "number"
      && mustHaveResults(dimensions, ctx?.listing).some(r => r.verdict === VERDICTS.PARTIAL)) {
    if (base > profile.mustHavePartialCap) caps.push("must_have_partial");
    base = Math.min(base, profile.mustHavePartialCap);
  }
  if (typeof profile?.mustHavePartialCap === "number" && unconfirmedMustHaves(dimensions, ctx?.listing).length > 0) {
    if (base > profile.mustHavePartialCap) caps.push("must_have_unknown");
    base = Math.min(base, profile.mustHavePartialCap);
  }
  // Optional post-process step (used by MARKETPLACE to apply the verification
  // bonus + over-radius distance penalty that the legacy matchScore had).
  if (typeof profile?.postProcess === "function") {
    return { score: profile.postProcess(base, { dimensions, ...ctx }), parts, caps };
  }
  return { score: base, parts, caps };
}

// ──────────────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Evaluate an applicant↔case fit.
 *
 * @param {Object} applicant - Therapist/applicant data. Expected fields:
 *   skills[], settings[], availability[], distanceMi, maxDistance, minHours,
 *   maxHours, experience, employments[]|employment, ageGroups[],
 *   payerEnrollments[].
 * @param {Object} listing - Case data. Expected fields: skills[]|requiredSkills[],
 *   settings[]|setting, availability[]|serviceDays[], maxDistance|distanceMax,
 *   hours, experienceMin, employments[]|employment, ageRange|ageGroup,
 *   payerTypes[]|payers[].
 * @param {Object} [options]
 * @param {Object} [options.profile] - One of PROFILES.* (default AGENCY_REVIEW).
 *
 * @returns {{
 *   overall: number|null,
 *   verdict: string|null,
 *   tier: string|null,
 *   summary: string|null,
 *   recommendation: string|null,
 *   dimensions: Object
 * }|null}
 */
export function evaluateApplicantFit(applicant, listing, options = {}) {
  if (!listing) return null;
  const profile = options.profile || PROFILES.AGENCY_REVIEW;
  const app = applicant || {};
  const dimensions = computeDimensions(app, listing);
  const detailed   = computeOverallDetailed(dimensions, profile, { applicant: app, listing });
  const overall    = detailed.score;
  // Only profiles with must haves (AGENCY_REVIEW) use the must-have explanations and the score-based tier; the
  // discovery and marketplace profiles keep their legacy rules so those surfaces do not shift.
  const withMusts = profile.mustHaveCap != null;
  const rules = buildSummaryAndRecommendation(dimensions, listing, { mustHaves: withMusts });
  const { summary, recommendation } = rules;
  const tier = withMusts ? (fitTierForScore(overall) || rules.tier) : rules.tier;
  return {
    overall,
    verdict: tier,         // alias to keep verb consistent with dimension shape
    tier,
    summary,
    recommendation,
    dimensions,
    breakdown: { parts: detailed.parts, caps: detailed.caps },
    mustHaves: mustHaveResults(dimensions, listing),
    missedMustHaves: missedMustHaves(dimensions, listing),
    unconfirmedMustHaves: unconfirmedMustHaves(dimensions, listing),
  };
}

/**
 * True when a therapist has the "minimal" profile (role + ZIP only) — none of
 * the dimensions the fit engine scores on (skills, availability, settings).
 * Such a profile is a real, contactable lead but is NOT scoreable: forcing a
 * number would either fabricate quality or unfairly read as a low match. Agency
 * surfaces show "profile in progress / fit pending" for these instead of a %.
 * @param {Object} applicant
 * @returns {boolean}
 */
export function isMinimalTherapistProfile(applicant) {
  if (!applicant) return false;
  return (applicant.skills || []).length === 0
    && (applicant.availability || []).length === 0
    && (applicant.settings || []).length === 0;
}

/**
 * Thin convenience wrapper that returns just the overall % — same shape as
 * the legacy applicationFit.computeMatchScore. Existing call sites can be
 * migrated to this without changing what they read.
 *
 * Returns null for a minimal (role+ZIP) profile: there isn't enough profile to
 * score honestly, and every consumer already guards on a null score (so no fake
 * number is shown). Consumers use isMinimalTherapistProfile() to render the
 * positive "profile in progress" treatment in its place.
 */
export function evaluateApplicantFitScore(applicant, listing, options = {}) {
  if (isMinimalTherapistProfile(applicant)) return null;
  const r = evaluateApplicantFit(applicant, listing, options);
  return r ? r.overall : null;
}

/**
 * Returns just the structured per-dimension assessments without computing
 * an overall % or banner copy. Useful for surfaces that need the canonical
 * dimension data (skills overlap, setting match, age fraction, …) but apply
 * their own aggregation rules — e.g. discoveryFit which is role-aware and
 * uses a soft distance fall-off rather than the engine's binary distance
 * verdict.
 *
 * Returns an empty {} when listing is missing.
 *
 * @param {Object} applicant
 * @param {Object} listing
 * @returns {Object} Same shape as the `.dimensions` field of evaluateApplicantFit.
 */
export function evaluateApplicantDimensions(applicant, listing) {
  if (!listing) return {};
  return computeDimensions(applicant || {}, listing);
}

/**
 * Canonical skill-overlap computation. Mirrors the internal assessSkills
 * helper but takes raw arrays so callers (like caseRecommendation's
 * hire-probability model) don't have to pretend they have a full
 * applicant + listing object just to compute a skill gap list.
 *
 * @param {string[]} required - Required skills from the listing.
 * @param {string[]} have - Skills the candidate has.
 * @param {Object} [options]
 * @param {boolean} [options.caseInsensitive=false] - Normalize both sides
 *   to lowercase before comparison. Used by caseRecommendation, which has
 *   always been case-insensitive defensively.
 * @returns {{matched: string[], missing: string[], extra: string[],
 *   fraction: number, coveragePct: number}} Same matched/missing arrays
 *   shape as dimensions.skills, plus `coveragePct` (0-100) for convenience.
 *   Original-case strings are preserved in the returned arrays.
 */
export function compareSkills(required, have, options = {}) {
  const req = Array.isArray(required) ? required : [];
  const has = Array.isArray(have) ? have : [];
  if (options.caseInsensitive) {
    // Delegate to the engine's normalized matcher — case AND separator
    // insensitive (e.g. "Feeding/Mealtime" ↔ "Feeding","Mealtime") — so a
    // hire-chance computed from this AGREES with the "% fit" from assessSkills.
    // Was lowercase-only, which split-token cases made disagree (David fix).
    const matched = matchedBy(req, has);
    const missing = missingBy(req, has);
    const extra   = extraBy(has, req);
    const fraction = req.length === 0 ? 1 : matched.length / req.length;
    return { matched, missing, extra, fraction, coveragePct: Math.round(fraction * 100) };
  }
  const matched = req.filter(s => has.includes(s));
  const missing = req.filter(s => !has.includes(s));
  const extra   = has.filter(s => !req.includes(s));
  const fraction = req.length === 0 ? 1 : matched.length / req.length;
  return { matched, missing, extra, fraction, coveragePct: Math.round(fraction * 100) };
}
